import { createSelector } from "@reduxjs/toolkit";
import { projectMembersSelector } from "@store/projects/projects-selector";
/**
 * Returns the current logged in user.
 *
 * @param state store state
 * @returns current logged in user
 */
export const currentUserSelector = createSelector(state => state.user, user => {
  return user.currentUser;
});

/**
 * Returns  extra information about current logged in user.
 * For details like name, email, etc. use currentUser.
 * This is only used for information like login method, SSO provider, etc.
 *
 * @param state store state
 * @returns current logged in user
 */
export const loggedInUserSelector = createSelector(state => state.user, user => {
  return user.loggedInUser ?? null;
});

/**
 * Returns true if user is logged in, otherwise false.
 *
 * @param state store state
 * @returns true if user is logged in, otherwise false.
 */
export const isUserLoggedInSelector = createSelector(state => state.user, user => {
  return !!user.loggedInUser;
});

/**
 * Returns the role of current logged in user in the project
 *
 * @param state store state
 * @returns role of current user
 */
export const currentUserRoleInProjectSelector = createSelector(state => state.user, projectMembersSelector, (user, projectMembers) => {
  const currentUserAsMember = projectMembers.find(member => member.identity === user.currentUser?.identity);
  return currentUserAsMember?.role;
});

/**
 * Returns the fetching properties of the user slice.
 */
export const fetchingUserFlagsSelector = createSelector(state => state.user, user => {
  return user.fetching;
});

/**
 * Returns true if the provided user identity is related to the current user
 */
export function isCurrentUserSelector(userIdentity) {
  return createSelector(currentUserSelector, currentUser => {
    return currentUser?.identity === userIdentity;
  });
}

/**
 * Returns the email of the user that is being updated.
 */
export const updatedEmailSelector = createSelector(state => state.user, user => {
  return user.updatedEmail;
});

/** Returns the SDB splash screens based on view status */
export function splashScreenTopicsByStatusSelector(status) {
  return createSelector(state => state.user, loggedInUserSelector, (user, loggedInUser) => {
    // If loggedInUser data is not available, then it means that the FUE flags determining
    // the splashScreenStatus is not available yet. In this case, we need to hide the splash screens otherwise,
    // we will show them all every time user refreshes the page.
    // TODO: Make sure loggedInUser is always available: https://faro01.atlassian.net/browse/ST-1623
    if (loggedInUser === null) {
      return [];
    }
    return Object.keys(user.splashScreens.sdb).filter(topic => user.splashScreens.sdb[topic] === status);
  });
}